// src/pages/po/SearchSupplierModalContent.tsx
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faIndustry } from '@/icons';
import { apiGet } from '../../services/api';
import ScrollableTable from '../../components/common/ScrollableTable';
import SearchButton from '../../components/common/SearchButton';
import SaveButton from '../../components/common/SaveButton';
import { usePaginatedFetch } from '../../hooks/usePaginatedFetch';
import axios from 'axios';
import { Loja } from '../../types/parametrizacao/Loja';
import './SearchSupplierModalContent.css';
import { useApiCall } from '../../hooks/useApiCall';
import { useAuth } from '../../contexts/AuthContext';
import { showAlert } from '../../contexts/DialogContext';
import { Toast } from '../../contexts/ToastContext';
import { logger } from '@/utils/logger';

interface Supplier {
  codUsuario: number;
  nome: string;
  cpfCnpj: string;
}

interface SearchSupplierModalContentProps {
  ordemCompraId: number;
  theme: 'dark' | 'light';
  onClose: () => void;
  onSupplierUpdate: (data: {
    codFornecedor: number;
    nomeFornecedor: string;
    dataEntrega: string;
    countDiasEntrega: number;
  }) => void;
  selectedItemIds: number[];
}

const SearchSupplierModalContent: React.FC<SearchSupplierModalContentProps> = ({
  ordemCompraId: _ordemCompraId,
  theme,
  onClose,
  onSupplierUpdate,
  selectedItemIds,
}) => {
  const { userId } = useAuth();
  const [searchValue, setSearchValue] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [dataEntrega, setDataEntrega] = useState<string>('');
  const [lojas, setLojas] = useState<Loja[]>([]); // Novo estado para lojas
  const [selectedLojaId, setSelectedLojaId] = useState<number | null>(null); // Novo estado para loja selecionada
  const { callApi, isLoading: isSaving } = useApiCall();
  const searchValueRef = useRef(searchValue);

  // Limpa estados ao abrir o modal
  useEffect(() => {
    setSearchValue('');
    setSelectedSupplier(null);
    setDataEntrega('');
    setSelectedLojaId(null); // Limpa loja selecionada
    reset();
  }, []);

  // Fetch lojas no mount — endpoint user-scoped (nao exige lojas_acesso.listar,
  // gateada para uso administrativo). ROLE_USER/ROLE_ADMIN sem essa permissao
  // recebiam 400 do endpoint global anterior (/v1/lojas-acesso?paged=false).
  useEffect(() => {
    if (!userId) return;
    const fetchLojas = async () => {
      try {
        const res = await apiGet(`/v1/lojas-acesso/usuario/${userId}?paged=false`);
        // Só lojas ATIVAS podem receber entrega (inativa → 400 no backend). O endpoint
        // /usuario/{id} retorna inativas (histórico) — filtramos aqui no seletor de
        // loja de entrega. Vale inclusive para ROLE_MASTER (que recebe inativas vinculadas).
        setLojas(
          (res.data as Loja[])
            .filter((loja) => loja.ativo === true)
            .sort((a, b) => a.nomeFantasia.localeCompare(b.nomeFantasia))
        );
      } catch (err) {
        logger.error('Erro ao buscar lojas:', err);
        await showAlert('Erro ao buscar lojas de entrega. Tente novamente.', { variant: 'error' });
      }
    };
    fetchLojas();
  }, [userId]);

  React.useEffect(() => {
    searchValueRef.current = searchValue;
  }, [searchValue]);

  const fetchSuppliers = useCallback(
    async (
      pageNumber: number,
      _currentItems: Supplier[]
    ): Promise<{ items: Supplier[]; totalItems: number }> => {
      const currentSearchValue = searchValueRef.current;
      if (!currentSearchValue || currentSearchValue.length < 3) {
        return { items: [], totalItems: 0 };
      }
      const queryParams = new URLSearchParams({
        valor: currentSearchValue,
        page: pageNumber.toString(),
        size: '10',
      });
      const endpoint = `/v1/busca-inteligente/pessoas-connection?${queryParams.toString()}`;
      try {
        const res = await apiGet(endpoint);
        const items = res.data.content.map((item: Supplier) => ({
          codUsuario: item.codUsuario,
          nome: item.nome,
          cpfCnpj: item.cpfCnpj,
        }));
        return { items, totalItems: res.data.page.totalElements };
      } catch (err) {
        logger.error('Erro ao buscar fornecedores:', err);
        let errorMessage = 'Erro ao buscar fornecedores. Tente novamente.';
        if (axios.isAxiosError(err)) {
          errorMessage = err.response?.data?.error || errorMessage;
        }
        throw new Error(errorMessage);
      }
    },
    []
  );

  const { items, hasMore, isLoading, loadMore, reset } = usePaginatedFetch<Supplier>({
    fetchFunction: fetchSuppliers,
    pageSize: 10,
    uniqueKey: 'codUsuario',
    initialPage: 0,
  });

  const handleSearch = async () => {
    if (searchValue.length < 3) {
      await showAlert('Digite pelo menos 3 caracteres para buscar.', { variant: 'warning' });
      return;
    }
    reset();
  };

  const handleSelectChange = (selectedItems: Supplier[]) => {
    // Seleção única: apenas o último item selecionado é mantido
    const newSelected = selectedItems.length > 0 ? selectedItems[0] : null;
    setSelectedSupplier(newSelected);
  };

  const handleSave = async () => {
    if (!selectedSupplier || !dataEntrega || !selectedLojaId) {
      await showAlert('Selecione um fornecedor, informe a data de entrega e selecione a loja de entrega.', { variant: 'warning' });
      return;
    }

    try {
      // Sem sufixo de offset: o DTO do backend é LocalDateTime (timezone-naive).
      // Enviar 'Z' funcionava por tolerância do Jackson, não por contrato (issue #17).
      const isoDate = `${dataEntrega}T00:00:00`;

      const payload = {
        codFornecedor: selectedSupplier.codUsuario,
        dataEntrega: isoDate,
        preOrdemItemIds: selectedItemIds,
        lojaEntregaId: selectedLojaId,
      };

      await callApi('post', '/v1/fornecedores-cotacao', payload, {
        onSuccess: (cotacao) => {
          Toast.success(cotacao.message || 'Cotação criada com sucesso');
          onSupplierUpdate({
            codFornecedor: cotacao.codFornecedor,
            nomeFornecedor: cotacao.nome || selectedSupplier?.nome || 'Fornecedor não informado',
            dataEntrega: cotacao.dataEntrega,
            countDiasEntrega: cotacao.countDiasEntrega,
          });
          onClose();
        },
      });
    } catch (err) {
      logger.error('Erro ao criar cotação:', err);
      await showAlert('Erro ao criar cotação. Tente novamente.', { variant: 'error' });
    }
    // Removido finally: gerenciado pelo hook.
  };

  const columns = [
    {
      header: 'Código',
      render: (item: Supplier) => item.codUsuario,
    },
    {
      header: 'Nome',
      render: (item: Supplier) => item.nome,
    },
    {
      header: 'CPF/CNPJ',
      render: (item: Supplier) => item.cpfCnpj,
    },
  ];

  return (
    <div className={`search-supplier-modal-content ${theme}`}>
      <h2><FontAwesomeIcon icon={faIndustry} /> Buscar e Inserir Fornecedor</h2>
      <div className="search-form">
        <input
          type="text"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          placeholder="Digite nome ou CPF/CNPJ"
          className="search-input"
        />
        <SearchButton
          isLoading={isLoading}
          onClick={handleSearch}
          disabled={searchValue.length < 3}
        >
          Procurar
        </SearchButton>
      </div>
      <ScrollableTable<Supplier>
        data={items}
        hasMore={hasMore}
        loadMore={loadMore}
        isLoading={isLoading}
        columns={columns}
        onSelectChange={handleSelectChange}
        emptyMessage={searchValue.length < 3 ? "Digite pelo menos 3 caracteres para buscar." : "Nenhum fornecedor encontrado."}
        showCheckboxes={true}
        renderCheckbox={() => true}
        singleSelect={true}
        isModal={true}
      />
      <div className="data-entrega-field">
        <label htmlFor="dataEntrega">Previsão de Entrega:</label>
        <input
          id="dataEntrega"
          type="date"
          value={dataEntrega}
          onChange={(e) => setDataEntrega(e.target.value)}
          className="date-input"
        />
      </div>
      <div className="loja-entrega-field"> {/* Novo campo após previsão */}
        <label htmlFor="lojaEntrega">Loja de Entrega:</label>
        <select
          id="lojaEntrega"
          value={selectedLojaId || ''}
          onChange={(e) => setSelectedLojaId(Number(e.target.value) || null)}
          className="select-input"
        >
          <option value="">Selecione uma loja</option>
          {lojas.map((loja) => (
            <option key={loja.id} value={loja.id}>
              {loja.nomeFantasia}
            </option>
          ))}
        </select>
      </div>
      <div className="form-actions">
        <SaveButton
          isLoading={isSaving}
          onClick={handleSave}
          disabled={!selectedSupplier || !dataEntrega || !selectedLojaId || isSaving}
        />
      </div>
    </div>
  );
};

export default SearchSupplierModalContent;